import React from "react";
import {
  LayoutDashboard,
  BookOpen,
  BarChart2,
  FileText,
  Sparkles,
  UserCheck,
  Shield,
  Users,
  ShoppingBag,
  CreditCard,
  Building,
  Bus,
  Package,
  Settings,
} from "lucide-react";

export interface NavItemConfig {
  label: string;
  route: string;
  iconName: string;
  badge?: string;
  roles?: string[]; // If omitted, visible to all authenticated roles
  permission?: string;
  isFinished?: boolean; // Default true
}

export interface NavGroupConfig {
  groupLabel: string;
  items: NavItemConfig[];
}

export const SIDEBAR_CONFIG: NavGroupConfig[] = [
  {
    groupLabel: "Main",
    items: [
      {
        label: "Dashboard",
        route: "/dashboard",
        iconName: "LayoutDashboard",
        isFinished: true,
      },
    ],
  },
  {
    groupLabel: "Academics & LMS",
    items: [
      {
        label: "Academic Courses",
        route: "/academics/departments",
        iconName: "BookOpen",
        roles: ["super_admin", "administrator", "principal", "dean", "hod", "faculty", "student"],
        isFinished: true,
      },
      {
        label: "Class Timetables",
        route: "/timetable",
        iconName: "BarChart2",
        roles: ["super_admin", "administrator", "principal", "dean", "hod", "faculty", "student", "parent"],
        isFinished: true,
      },
      {
        label: "Examinations",
        route: "/examinations",
        iconName: "FileText",
        roles: ["super_admin", "administrator", "principal", "dean", "hod", "faculty", "student", "parent", "exam_controller"],
        isFinished: true,
      },
      {
        label: "LMS Learning Paths",
        route: "/lms",
        iconName: "BookOpen",
        roles: ["super_admin", "administrator", "principal", "dean", "hod", "faculty", "student"],
        isFinished: true,
      },
      {
        label: "Quiz Center",
        route: "/quiz",
        iconName: "Sparkles",
        roles: ["super_admin", "administrator", "principal", "dean", "hod", "faculty", "student"],
        isFinished: true,
      },
      {
        label: "Grades & Results",
        route: "/results",
        iconName: "UserCheck",
        roles: ["super_admin", "administrator", "principal", "dean", "hod", "faculty", "student", "parent", "exam_controller"],
        isFinished: true,
      },
      {
        label: "Certificates",
        route: "/certificates",
        iconName: "Shield",
        roles: ["super_admin", "administrator", "principal", "dean", "hod", "faculty", "student"],
        isFinished: true,
      },
      {
        label: "AI Assistant Hub",
        route: "/ai",
        iconName: "Sparkles",
        roles: ["super_admin", "administrator", "principal", "dean", "hod", "faculty", "student", "research_head"],
        isFinished: true,
      },
    ],
  },
  {
    groupLabel: "Directory & Portals",
    items: [
      {
        label: "Student Directory",
        route: "/students",
        iconName: "Users",
        roles: ["super_admin", "administrator", "principal", "dean", "hod", "faculty", "admission_officer"],
        isFinished: true,
      },
      {
        label: "Admissions Portal",
        route: "/admissions",
        iconName: "UserCheck",
        roles: ["super_admin", "administrator", "principal", "admission_officer"],
        isFinished: true,
      },
      {
        label: "Faculty & Staff Directory",
        route: "/staff",
        iconName: "UserCheck",
        roles: ["super_admin", "administrator", "principal", "dean", "hod", "hr_manager", "staff"],
        isFinished: true,
      },
      {
        label: "Parent Guardian Portal",
        route: "/parents",
        iconName: "Users",
        roles: ["super_admin", "administrator", "principal", "parent"],
        isFinished: true,
      },
      {
        label: "HR & Personnel",
        route: "/hr",
        iconName: "Users",
        roles: ["super_admin", "administrator", "principal", "hr_manager", "staff"],
        isFinished: true,
      },
      {
        label: "Placement & Careers",
        route: "/placement",
        iconName: "ShoppingBag",
        roles: ["super_admin", "administrator", "principal", "placement", "faculty", "student", "staff"],
        isFinished: true,
      },
      {
        label: "Alumni Network",
        route: "/alumni",
        iconName: "Sparkles",
        roles: ["super_admin", "administrator", "principal", "placement", "student"],
        isFinished: true,
      },
    ],
  },
  {
    groupLabel: "Finance & Operations",
    items: [
      {
        label: "Fee Structures",
        route: "/fees",
        iconName: "CreditCard",
        roles: ["super_admin", "administrator", "principal", "accountant", "student", "parent"],
        isFinished: true,
      },
      {
        label: "Payment Collections",
        route: "/payments",
        iconName: "CreditCard",
        roles: ["super_admin", "administrator", "accountant", "student", "parent"],
        isFinished: true,
      },
      {
        label: "Scholarships",
        route: "/scholarships",
        iconName: "CreditCard",
        roles: ["super_admin", "administrator", "accountant", "student"],
        isFinished: true,
      },
      {
        label: "Payroll & Salaries",
        route: "/payroll",
        iconName: "CreditCard",
        roles: ["super_admin", "administrator", "accountant", "hr_manager", "staff"],
        isFinished: true,
      },
      {
        label: "Procurement & Orders",
        route: "/procurement",
        iconName: "ShoppingBag",
        roles: ["super_admin", "administrator", "accountant", "staff"],
        isFinished: true,
      },
    ],
  },
  {
    groupLabel: "Campus Services",
    items: [
      {
        label: "Digital Library",
        route: "/library",
        iconName: "BookOpen",
        roles: ["super_admin", "administrator", "principal", "librarian", "faculty", "student", "staff"],
        isFinished: true,
      },
      {
        label: "Document Center",
        route: "/documents",
        iconName: "FileText",
        roles: ["super_admin", "administrator", "principal", "dean", "hod", "faculty", "student", "parent", "hr_manager", "accountant", "staff", "it_support", "librarian", "hostel_warden", "placement", "transport_manager"],
        isFinished: true,
      },
      {
        label: "Hostels & Housing",
        route: "/hostel",
        iconName: "Building",
        roles: ["super_admin", "administrator", "hostel_warden", "student", "parent", "staff"],
        isFinished: true,
      },
      {
        label: "Transport Fleet",
        route: "/transport",
        iconName: "Bus",
        roles: ["super_admin", "administrator", "transport_manager", "student", "parent", "staff"],
        isFinished: true,
      },
      {
        label: "Inventory & Stores",
        route: "/inventory",
        iconName: "Package",
        roles: ["super_admin", "administrator", "accountant", "staff"],
        isFinished: true,
      },
      {
        label: "Assets & Maintenance",
        route: "/assets",
        iconName: "Building",
        roles: ["super_admin", "administrator", "it_support", "staff"],
        isFinished: true,
      },
      {
        label: "Visitor & Security",
        route: "/visitor",
        iconName: "Shield",
        roles: ["super_admin", "administrator", "it_support", "staff"],
        isFinished: true,
      },
    ],
  },
  {
    groupLabel: "Governance & Settings",
    items: [
      {
        label: "Analytics & Reports",
        route: "/reports/naac-nirf",
        iconName: "BarChart2",
        roles: ["super_admin", "administrator", "principal", "dean", "staff"],
        isFinished: true,
      },
      {
        label: "RBAC Matrix",
        route: "/rbac/roles",
        iconName: "Shield",
        roles: ["super_admin", "administrator"],
        isFinished: true,
      },
      {
        label: "Settings",
        route: "/profile/security",
        iconName: "Settings",
        isFinished: true,
      },
    ],
  },
];
